import * as THREE from 'three';

/** Premium neon + gold + paper — reads “finish line” not “office party”. */
const STREAMER = [
  0xff2d55, 0x00d4ff, 0xffd60a, 0xb967ff, 0x00ffaa, 0xff6b35, 0xfff1e0, 0xc9a227,
];
const SPARK_HOT = [0xffee88, 0xffffff, 0xffaa44, 0x88ddff];

function rgb(hex: number, out: number[]): void {
  out[0] = ((hex >> 16) & 0xff) / 255;
  out[1] = ((hex >> 8) & 0xff) / 255;
  out[2] = (hex & 0xff) / 255;
}

/** One confetti cloud: streamers + optional additive glitter child. */
function spawnConfettiCloud(
  scene: THREE.Scene,
  cx: number,
  cy: number,
  cz: number,
  nStream: number,
  nGlitter: number,
  spreadXZ: number,
  spawnY0: number,
  spawnY1: number,
  heading: number,
  /** 0 = overhead deluge, 1 = left cannon, 2 = right, 3 = forward fan */
  pattern: number,
): void {
  const sinH = Math.sin(heading);
  const cosH = Math.cos(heading);
  const rx = cosH;
  const rz = -sinH;
  const fx = sinH;
  const fz = cosH;

  const positions = new Float32Array(nStream * 3);
  const velocities: THREE.Vector3[] = [];
  const tumble: number[] = [];
  const col = new Float32Array(nStream * 3);
  const tmp = [0, 0, 0];

  for (let i = 0; i < nStream; i++) {
    let px = cx + (Math.random() - 0.5) * 2.5;
    let py = cy + spawnY0 + Math.random() * (spawnY1 - spawnY0);
    let pz = cz + (Math.random() - 0.5) * 2.5;
    let vx: number;
    let vy: number;
    let vz: number;

    if (pattern === 0) {
      px = cx + (Math.random() - 0.5) * spreadXZ;
      pz = cz + (Math.random() - 0.5) * spreadXZ;
      vy = -1.2 - Math.random() * 5;
      vx = (Math.random() - 0.5) * 16;
      vz = (Math.random() - 0.5) * 16;
    } else if (pattern === 1) {
      px = cx - rx * 14 + (Math.random() - 0.5) * 3;
      pz = cz - rz * 14 + (Math.random() - 0.5) * 3;
      py = cy + 2 + Math.random() * 6;
      vx = rx * (10 + Math.random() * 14) + (Math.random() - 0.5) * 4;
      vy = 3 + Math.random() * 9;
      vz = rz * (10 + Math.random() * 14) + (Math.random() - 0.5) * 4;
    } else if (pattern === 2) {
      px = cx + rx * 14 + (Math.random() - 0.5) * 3;
      pz = cz + rz * 14 + (Math.random() - 0.5) * 3;
      py = cy + 2 + Math.random() * 6;
      vx = -rx * (10 + Math.random() * 14) + (Math.random() - 0.5) * 4;
      vy = 3 + Math.random() * 9;
      vz = -rz * (10 + Math.random() * 14) + (Math.random() - 0.5) * 4;
    } else {
      px = cx + fx * (4 + Math.random() * 5) + (Math.random() - 0.5) * 4;
      pz = cz + fz * (4 + Math.random() * 5) + (Math.random() - 0.5) * 4;
      py = cy + 5 + Math.random() * 8;
      vx = (Math.random() - 0.5) * 10 + fx * 8;
      vy = 2 + Math.random() * 11;
      vz = (Math.random() - 0.5) * 10 + fz * 8;
    }

    positions[i * 3] = px;
    positions[i * 3 + 1] = py;
    positions[i * 3 + 2] = pz;
    velocities.push(new THREE.Vector3(vx, vy, vz));
    tumble.push((Math.random() - 0.5) * 22);
    const hex = STREAMER[i % STREAMER.length]!;
    rgb(hex, tmp);
    col[i * 3] = tmp[0]!;
    col[i * 3 + 1] = tmp[1]!;
    col[i * 3 + 2] = tmp[2]!;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const mat = new THREE.PointsMaterial({
    size: 0.52,
    vertexColors: true,
    transparent: true,
    opacity: 0.97,
    depthWrite: false,
    sizeAttenuation: true,
  });
  const pts = new THREE.Points(geo, mat);
  scene.add(pts);

  let glitterPts: THREE.Points | null = null;
  let glitterGeo: THREE.BufferGeometry | null = null;
  let glitterMat: THREE.PointsMaterial | null = null;
  const gPos = new Float32Array(nGlitter * 3);
  const gVel: THREE.Vector3[] = [];
  const gCol = new Float32Array(nGlitter * 3);
  const gTumble: number[] = [];

  if (nGlitter > 0) {
    for (let i = 0; i < nGlitter; i++) {
      gPos[i * 3] = cx + (Math.random() - 0.5) * spreadXZ * 0.85;
      gPos[i * 3 + 1] = cy + spawnY0 + Math.random() * (spawnY1 - spawnY0);
      gPos[i * 3 + 2] = cz + (Math.random() - 0.5) * spreadXZ * 0.85;
      gVel.push(
        new THREE.Vector3(
          (Math.random() - 0.5) * 12,
          -2 - Math.random() * 6,
          (Math.random() - 0.5) * 12,
        ),
      );
      gTumble.push((Math.random() - 0.5) * 30);
      const hx = SPARK_HOT[i % SPARK_HOT.length]!;
      rgb(hx, tmp);
      gCol[i * 3] = tmp[0]!;
      gCol[i * 3 + 1] = tmp[1]!;
      gCol[i * 3 + 2] = tmp[2]!;
    }
    glitterGeo = new THREE.BufferGeometry();
    glitterGeo.setAttribute('position', new THREE.BufferAttribute(gPos, 3));
    glitterGeo.setAttribute('color', new THREE.BufferAttribute(gCol, 3));
    glitterMat = new THREE.PointsMaterial({
      size: 0.16,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    glitterPts = new THREE.Points(glitterGeo, glitterMat);
    scene.add(glitterPts);
  }

  const t0 = performance.now();
  const lifeMs = 3600;
  let prev = t0;
  const gFluff = 6.2;
  const drag = 0.994;

  function tick(now: number): void {
    const dt = Math.min(0.045, (now - prev) / 1000);
    prev = now;
    const elapsed = now - t0;
    const t = elapsed / lifeMs;
    if (t >= 1) {
      scene.remove(pts);
      geo.dispose();
      mat.dispose();
      if (glitterPts && glitterGeo && glitterMat) {
        scene.remove(glitterPts);
        glitterGeo.dispose();
        glitterMat.dispose();
      }
      return;
    }

    const arr = geo.attributes.position.array as Float32Array;
    const windX = Math.sin(elapsed * 0.0017) * 3.2;
    const windZ = Math.cos(elapsed * 0.0014) * 2.8;

    for (let i = 0; i < nStream; i++) {
      const v = velocities[i]!;
      v.y -= gFluff * dt;
      v.x += (windX + Math.sin(elapsed * 0.002 + tumble[i]!) * 4.5) * dt;
      v.z += (windZ + Math.cos(elapsed * 0.0023 + tumble[i]!) * 4.5) * dt;
      v.multiplyScalar(Math.pow(drag, dt * 60));
      const tx = tumble[i]!;
      v.x += Math.sin(elapsed * 0.003 + tx) * 1.8 * dt;
      v.z += Math.cos(elapsed * 0.0025 + tx * 1.1) * 1.8 * dt;
      arr[i * 3] += v.x * dt;
      arr[i * 3 + 1] += v.y * dt;
      arr[i * 3 + 2] += v.z * dt;
    }
    geo.attributes.position.needsUpdate = true;
    mat.opacity = 0.97 * (1 - Math.pow(t, 1.65) * 0.92);

    if (glitterPts && glitterGeo) {
      const ga = glitterGeo.attributes.position.array as Float32Array;
      for (let i = 0; i < nGlitter; i++) {
        const v = gVel[i]!;
        v.y -= (gFluff + 4) * dt;
        v.x += Math.sin(elapsed * 0.004 + gTumble[i]!) * 6 * dt;
        v.z += Math.cos(elapsed * 0.0036 + gTumble[i]!) * 6 * dt;
        v.multiplyScalar(Math.pow(0.991, dt * 60));
        ga[i * 3] += v.x * dt;
        ga[i * 3 + 1] += v.y * dt;
        ga[i * 3 + 2] += v.z * dt;
      }
      glitterGeo.attributes.position.needsUpdate = true;
      glitterMat!.opacity = 0.9 * (1 - t);
    }

    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

function scheduleFireworkShow(
  scene: THREE.Scene,
  cx: number,
  cz: number,
): void {
  const bursts: { delay: number; ox: number; oz: number; n: number; peak: number; hue: number }[] = [
    { delay: 0.08, ox: -5, oz: 6, n: 72, peak: 26, hue: 0 },
    { delay: 0.42, ox: 9, oz: -2, n: 68, peak: 24, hue: 1 },
    { delay: 0.88, ox: -8, oz: -5, n: 76, peak: 28, hue: 2 },
    { delay: 1.35, ox: 4, oz: 9, n: 64, peak: 22, hue: 0 },
    { delay: 1.85, ox: -3, oz: -10, n: 70, peak: 27, hue: 1 },
    { delay: 2.35, ox: 11, oz: 4, n: 66, peak: 25, hue: 2 },
    { delay: 2.85, ox: -6, oz: 2, n: 80, peak: 29, hue: 0 },
  ];
  for (const b of bursts) {
    window.setTimeout(() => {
      spawnRisingShellThenBurst(scene, cx + b.ox, cz + b.oz, b.n, b.peak, b.hue);
    }, b.delay * 1000);
  }
}

/** Tracer rises ~0.35s, then spherical burst at apex (readable “shell” moment). */
function spawnRisingShellThenBurst(
  scene: THREE.Scene,
  x: number,
  z: number,
  n: number,
  peakY: number,
  hueVariant: number,
): void {
  let py = 4.5 + Math.random() * 2.5;
  let vy = 30 + Math.random() * 8;
  const g = 34;
  const t0 = performance.now();
  let prev = t0;

  const sGeo = new THREE.BufferGeometry();
  const sPos = new Float32Array([x, py, z]);
  sGeo.setAttribute('position', new THREE.BufferAttribute(sPos, 3));
  const sMat = new THREE.PointsMaterial({
    color: hueVariant === 0 ? 0xffeedd : hueVariant === 1 ? 0xccffee : 0xffddff,
    size: 0.42,
    transparent: true,
    opacity: 0.98,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const sPts = new THREE.Points(sGeo, sMat);
  scene.add(sPts);

  function tickRise(now: number): void {
    const dt = Math.min(0.045, (now - prev) / 1000);
    prev = now;
    vy -= g * dt;
    py += vy * dt;
    sPos[1] = py;
    sGeo.attributes.position.needsUpdate = true;
    const elapsedMs = now - t0;
    if (vy <= 0 || py >= peakY || elapsedMs > 520) {
      scene.remove(sPts);
      sGeo.dispose();
      sMat.dispose();
      oneFireworkBurst(scene, x, Math.min(py, peakY + 2), z, n, hueVariant);
      return;
    }
    requestAnimationFrame(tickRise);
  }
  requestAnimationFrame(tickRise);
}

function oneFireworkBurst(
  scene: THREE.Scene,
  x: number,
  y: number,
  z: number,
  n: number,
  hueVariant: number,
): void {
  const positions = new Float32Array(n * 3);
  const velocities: THREE.Vector3[] = [];
  const col = new Float32Array(n * 3);
  const tmp = [0, 0, 0];

  const palette =
    hueVariant === 0
      ? [0xffcc44, 0xff6622, 0xffeeaa, 0xffffff]
      : hueVariant === 1
        ? [0x44ffcc, 0x22aaff, 0xaaffff, 0xffffff]
        : [0xff66dd, 0xaa66ff, 0xffccff, 0xffffff];

  for (let i = 0; i < n; i++) {
    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    const ang = Math.random() * Math.PI * 2;
    const inc = Math.acos(2 * Math.random() - 1);
    const speed = 14 + Math.random() * 22;
    const sp = Math.sin(inc) * speed;
    velocities.push(
      new THREE.Vector3(
        Math.cos(ang) * sp,
        Math.cos(inc) * speed * 0.85,
        Math.sin(ang) * sp,
      ),
    );
    const hex = palette[i % palette.length]!;
    rgb(hex, tmp);
    col[i * 3] = tmp[0]!;
    col[i * 3 + 1] = tmp[1]!;
    col[i * 3 + 2] = tmp[2]!;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const mat = new THREE.PointsMaterial({
    size: 0.62,
    vertexColors: true,
    transparent: true,
    opacity: 1,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
  });
  const pts = new THREE.Points(geo, mat);
  scene.add(pts);

  const t0 = performance.now();
  const lifeMs = 1100;
  let prev = t0;

  function tick(now: number): void {
    const dt = Math.min(0.05, (now - prev) / 1000);
    prev = now;
    const elapsed = now - t0;
    const t = elapsed / lifeMs;
    if (t >= 1) {
      scene.remove(pts);
      geo.dispose();
      mat.dispose();
      return;
    }
    const arr = geo.attributes.position.array as Float32Array;
    const gAcc = 11;
    for (let i = 0; i < n; i++) {
      const v = velocities[i]!;
      v.y -= gAcc * dt;
      v.multiplyScalar(Math.pow(0.992, dt * 60));
      arr[i * 3] += v.x * dt;
      arr[i * 3 + 1] += v.y * dt;
      arr[i * 3 + 2] += v.z * dt;
    }
    geo.attributes.position.needsUpdate = true;
    mat.opacity = (1 - t) * (1 - t);
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

/**
 * High-density victory VFX: staggered streamer waves, glitter, staged fireworks.
 */
export function spawnVictoryCelebrationParticles(
  scene: THREE.Scene,
  cx: number,
  cy: number,
  cz: number,
  reducedMotion: boolean,
  carHeading: number,
): void {
  if (reducedMotion) {
    spawnConfettiCloud(scene, cx, cy, cz, 72, 20, 14, 10, 20, carHeading, 0);
    spawnConfettiCloud(scene, cx, cy, cz, 48, 0, 12, 8, 16, carHeading, 3);
    scheduleFireworkShow(scene, cx, cz);
    return;
  }

  spawnConfettiCloud(scene, cx, cy, cz, 300, 110, 22, 11, 26, carHeading, 0);
  window.setTimeout(() => {
    spawnConfettiCloud(scene, cx, cy, cz, 220, 70, 18, 8, 20, carHeading, 1);
  }, 240);
  window.setTimeout(() => {
    spawnConfettiCloud(scene, cx, cy, cz, 220, 70, 18, 8, 20, carHeading, 2);
  }, 480);
  window.setTimeout(() => {
    spawnConfettiCloud(scene, cx, cy, cz, 200, 90, 20, 6, 22, carHeading, 3);
  }, 720);
  window.setTimeout(() => {
    spawnConfettiCloud(scene, cx, cy, cz, 160, 60, 16, 14, 28, carHeading, 0);
  }, 1050);

  scheduleFireworkShow(scene, cx, cz);
}
